import { loadMulpay } from '@smbc-gp/mptoken-js'

export class TokenizationError extends Error {
  constructor(message, code, details) {
    super(message)
    this.name = 'TokenizationError'
    this.code = code
    this.details = details
  }
}

// タイムアウト設定（ミリ秒）
const TOKEN_REQUEST_TIMEOUT_MS = 30000 // トークン取得: 30秒

/**
 * タイムアウト付きでPromiseを実行
 * @param {Promise} promise - 実行するPromise
 * @param {number} timeoutMs - タイムアウト時間（ミリ秒）
 * @param {Error} timeoutError - タイムアウト時にスローするエラー
 * @returns {Promise} 結果またはタイムアウトエラー
 */
function withTimeout(promise, timeoutMs, timeoutError) {
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => reject(timeoutError), timeoutMs)
  })
  return Promise.race([promise, timeoutPromise])
}

// mulpayインスタンスをキャッシュ
let mulpayInstance = null
let mulpayConfig = null

/**
 * mulpayインスタンスを取得（シングルトン）
 * @param {string} apiKey - GMO クレジットトークンAPI用のAPIキー
 * @param {string} publicKey - GMO クレジットトークンAPI用の公開鍵
 * @param {boolean} productionMode - 本番環境フラグ（true: 本番API、false: テストAPI）
 * @returns {Promise<Object>} mulpayインスタンス
 */
async function getMulpayInstance(apiKey, publicKey, productionMode) {
  // 設定が変わった場合は再初期化
  const configKey = `${apiKey}:${publicKey}:${productionMode}`
  if (mulpayInstance && mulpayConfig === configKey) {
    return mulpayInstance
  }

  mulpayInstance = await loadMulpay(apiKey, publicKey, undefined, true, productionMode)
  mulpayConfig = configKey
  return mulpayInstance
}

/**
 * GMO mptoken-js を使用してカード情報をトークン化
 *
 * @param {Object} cardData - カード情報
 * @param {string} cardData.cardNumber - カード番号
 * @param {string} cardData.expiryMonth - 有効期限月（2桁）
 * @param {string} cardData.expiryYear - 有効期限年（2桁）
 * @param {string} cardData.cardHolder - カード名義人
 * @param {string} cardData.securityCode - セキュリティコード
 * @param {Object} config - GMO設定
 * @param {string} config.apiKey - GMO クレジットトークンAPI用のAPIキー
 * @param {string} config.publicKey - GMO クレジットトークンAPI用の公開鍵
 * @param {boolean} config.productionMode - 本番環境フラグ（true: 本番API、false: テストAPI）
 * @returns {Promise<string>} GMOトークン
 * @throws {TokenizationError} トークン化失敗時
 */
export const tokenizeCard = async (cardData, config = {}) => {
  const { apiKey, publicKey, productionMode } = config

  // 設定値の検証
  if (!apiKey || !publicKey) {
    throw new TokenizationError('決済システムの設定が不正です。', 'INVALID_CONFIG')
  }

  try {
    // mulpayインスタンスを取得
    const mulpay = await getMulpayInstance(apiKey, publicKey, productionMode)

    // GMOパラメータを準備
    const gmoCardData = {
      cardno: cardData.cardNumber,
      expire: `${cardData.expiryYear}${cardData.expiryMonth}`,
      holdername: cardData.cardHolder,
      securitycode: cardData.securityCode,
    }

    // トークン取得（タイムアウト付き）
    const timeoutError = new TokenizationError('決済システムとの通信がタイムアウトしました。しばらく待ってから再度お試しください。', 'RESPONSE_TIMEOUT', {
      timeout: TOKEN_REQUEST_TIMEOUT_MS,
    })

    const response = await withTimeout(mulpay.getToken(gmoCardData), TOKEN_REQUEST_TIMEOUT_MS, timeoutError)

    if (response.resultCode === '000') {
      return response.tokenObject.token
    }
    throw new TokenizationError('カード情報の入力に誤りがあります。もう一度入力してください。', 'TOKENIZE_ERROR', response)
  } catch (error) {
    if (error instanceof TokenizationError) {
      throw error
    }
    throw new TokenizationError('決済システムの初期化に失敗しました。ページを再読み込みしてください。', 'INIT_ERROR', { originalError: error.message })
  }
}

/**
 * テスト用のトークン化関数（モック実装）
 * 本番環境では使用しないこと
 */
export const tokenizeCardMock = async () => {
  await new Promise((resolve) => setTimeout(resolve, 500))
  return 'mock_token_' + Date.now()
}
